/**
 * Immobilien – Lager (Katalog)
 * /immobilien/lager
 *
 * NT-VIERTEL-03-SCHALTZENTRALE (F7): reiner Katalog ohne Kaufweg — nur noch Sortiment/Preise/
 * Specs zum Browsen. Kaufen, Bebauen, Umziehen, Verkaufen und Öffnen laufen jetzt ausschließlich
 * im Tab VIERTEL (Crime Ops), das die tatsächliche Adresse/Fläche der Parzelle kennt. Bereits
 * gekaufte Lager werden im Lagerhaus (`/business/lagerhaus`) verwaltet, nicht mehr hier.
 */

import { api } from '../../services/api.js';
import { formatWarehouseName } from '../../utils/formatters.js';
import { navigate } from '../../router.js';
import { MOBILE_LAYOUT_QUERY } from '../../utils/mobile-layout.js';

// ─── Typen ────────────────────────────────────────────────────────────────────

interface CatalogWarehouse {
  subType:            string;
  label:              string;
  slots:              number;
  raidRiskPerFL:      number;
  minFlaecheQm:       number;
  purchasePriceCents: string;
  rentPerWeekCents:   string;
  lore:               string;
}

// ─── State ────────────────────────────────────────────────────────────────────

let catalogData: CatalogWarehouse[] | null = null;
let loadError:   string | null = null;

// ─── Helfer ───────────────────────────────────────────────────────────────────

function esc(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

function fmtNox(cents: string): string {
  return (parseInt(cents, 10) / 100).toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + ' ₦';
}

function raidRiskLabel(riskPerFL: number): string {
  return `${(riskPerFL * 100).toFixed(0)}% / FL`;
}

// ─── Render ───────────────────────────────────────────────────────────────────

function renderCatalogCard(w: CatalogWarehouse): string {
  return `
    <div class="prop-catalog-card">
      <div class="prop-catalog-header">
        <span class="prop-catalog-title">${esc(formatWarehouseName(w.label).toUpperCase())}</span>
        <span class="prop-catalog-slots">${String(w.slots)} SLOTS</span>
      </div>
      <p class="prop-catalog-lore">${esc(w.lore)}</p>
      <div class="prop-catalog-meta">
        <span class="prop-catalog-price">${fmtNox(w.purchasePriceCents)}</span>
        <span class="prop-catalog-stat">Miete: <span>${fmtNox(w.rentPerWeekCents)}/Woche</span></span>
        <span class="prop-catalog-stat">Razzia: <span style="color:var(--accent-orange);">${raidRiskLabel(w.raidRiskPerFL)}</span></span>
        <span class="prop-catalog-stat">Fläche ab: <span>${String(w.minFlaecheQm)} m²</span></span>
      </div>
    </div>
  `;
}

function renderPage(): string {
  let catalogHtml: string;
  if (loadError) {
    catalogHtml = `<p class="prop-empty" style="color:var(--accent-red);">Fehler: ${esc(loadError)}</p>`;
  } else if (catalogData) {
    catalogHtml = catalogData.map(renderCatalogCard).join('');
  } else {
    catalogHtml = '<p class="prop-loading">LADE...</p>';
  }

  return `
    <section class="dash-content" id="lager-root">
      <div class="prop-page-header">
        <div>
          <h2 class="prop-page-title">LAGER</h2>
          <p class="prop-page-subtitle">Infrastruktur für Materialien, Labor-Waren und Fahrzeuge.</p>
        </div>
      </div>

      <div class="prop-section">
        <p class="prop-viertel-hint">
          Kaufen, Umziehen und Verkaufen laufen jetzt im Viertel — dort siehst du auch, welche
          Fläche eine Parzelle hat und was darauf gebaut werden darf.
        </p>
        <button class="prop-btn prop-btn--buy" data-lager-zum-viertel>ZUM VIERTEL</button>
      </div>

      <div class="prop-section">
        <h3 class="prop-section-title">SORTIMENT</h3>
        <p class="prop-section-hint">Jedes Lager erhöht deine Gesamt-Lagerkapazität. Fahrzeuge belegen je 10 Slots.</p>
        <div class="prop-catalog-grid" id="lager-catalog">${catalogHtml}</div>
      </div>
    </section>
  `;
}

// ─── Event-Handling ───────────────────────────────────────────────────────────

function rerender(): void {
  const root = document.getElementById('lager-root');
  if (!root) return;
  const newRoot = document.createElement('div');
  newRoot.innerHTML = renderPage();
  const newSection = newRoot.firstElementChild;
  if (newSection) root.replaceWith(newSection);
  attachListeners();
}

function attachListeners(): void {
  const root = document.getElementById('lager-root');
  if (!root) return;

  root.addEventListener('click', (e) => {
    const target = e.target as Element;
    if (target.closest('[data-lager-zum-viertel]')) {
      sessionStorage.setItem('crimeops_tab', 'viertel');
      navigate('/business/crime-ops');
    }
  });
}

async function reload(): Promise<void> {
  try {
    const result = await api.get<{ warehouses: CatalogWarehouse[] }>('/properties/catalog');
    catalogData = result.warehouses;
    loadError = null;
  } catch (err) {
    loadError = err instanceof Error ? err.message : 'Unbekannter Fehler';
    console.error('[Lager] Katalog laden fehlgeschlagen:', err);
  }
  rerender();
}

// ─── Styles ───────────────────────────────────────────────────────────────────

function addStyles(): void {
  if (document.getElementById('prop-styles')) return;
  const s = document.createElement('style');
  s.id = 'prop-styles';
  s.textContent = `
    .prop-page-header {
      display: flex; align-items: flex-start; justify-content: space-between;
      padding: var(--space-3) var(--space-4) var(--space-2);
      border-bottom: 1px solid var(--border);
      flex-wrap: wrap; gap: var(--space-2);
    }
    .prop-page-title {
      font-family: var(--font-mono); font-size: var(--font-size-base); letter-spacing: 0.12em;
      color: var(--accent-gold); margin: 0 0 4px 0; text-transform: uppercase;
    }
    @media(min-width: 1024px) { .prop-page-title { font-size: var(--font-size-xl); } }
    .prop-page-subtitle {
      font-family: var(--font-mono); font-size: var(--font-size-xs); color: var(--text-muted);
      margin: 0; letter-spacing: 0.03em; line-height: 1.4;
    }

    .prop-section { padding: var(--space-4); border-bottom: 1px solid var(--border); }
    .prop-section:last-child { border-bottom: none; }
    .prop-section-title {
      font-family: var(--font-mono); font-size: var(--font-size-xs); letter-spacing: 0.10em;
      color: var(--text-muted); text-transform: uppercase;
      margin: 0 0 var(--space-3) 0;
    }
    .prop-section-hint {
      font-family: var(--font-mono); font-size: var(--font-size-xs); color: var(--text-muted);
      margin: 0 0 var(--space-3) 0; font-style: italic; line-height: 1.5;
    }
    .prop-viertel-hint {
      font-family: var(--font-mono); font-size: var(--font-size-xs); color: var(--text-muted);
      margin: 0 0 var(--space-3) 0; line-height: 1.5;
    }
    .prop-empty, .prop-loading {
      font-family: var(--font-mono); font-size: var(--font-size-xs); color: var(--text-muted);
      padding: var(--space-3) 0; line-height: 1.5;
    }

    .prop-btn {
      font-family: var(--font-mono); font-size: var(--font-size-xs); letter-spacing: 0.06em; text-transform: uppercase;
      padding: var(--space-2) var(--space-3); background: transparent; border-radius: var(--radius-btn);
      cursor: pointer; min-height: 40px; border: 1px solid var(--border); color: var(--text-muted);
      touch-action: manipulation; -webkit-tap-highlight-color: transparent;
    }
    .prop-btn--buy  { border-color: var(--accent-green);  color: var(--accent-green);  }

    /* ── Catalog Grid ── */
    .prop-catalog-grid {
      display: flex; flex-direction: column; gap: 1px;
      border: 1px solid var(--border); border-radius: var(--radius); overflow: hidden;
    }
    .prop-catalog-card {
      padding: var(--space-3) var(--space-4); background: var(--bg-surface);
      border-bottom: 1px solid var(--border);
    }
    .prop-catalog-card:last-child { border-bottom: none; }
    .prop-catalog-header {
      display: flex; justify-content: space-between; align-items: baseline;
      margin-bottom: 4px; gap: var(--space-2);
    }
    .prop-catalog-title {
      font-family: var(--font-mono); font-size: var(--font-size-base);
      color: var(--text-primary); letter-spacing: 0.05em;
    }
    .prop-catalog-slots {
      font-family: var(--font-mono); font-size: var(--font-size-sm);
      color: var(--accent-gold); white-space: nowrap;
    }
    .prop-catalog-lore {
      font-family: var(--font-mono); font-size: 13px;
      color: var(--text-italic); font-style: italic;
      margin: 0 0 var(--space-2) 0; line-height: 1.5;
    }
    .prop-catalog-meta {
      display: flex; flex-wrap: wrap; gap: var(--space-3);
      margin-bottom: 0; align-items: baseline;
    }
    .prop-catalog-price {
      font-family: var(--font-mono); font-size: var(--font-size-sm);
      color: var(--accent-gold); letter-spacing: 0.03em;
    }
    .prop-catalog-stat {
      font-family: var(--font-mono); font-size: 13px; color: var(--text-muted);
    }
    .prop-catalog-stat span { color: var(--text-primary); }

    @media ${MOBILE_LAYOUT_QUERY} {
      .prop-btn { width: 100%; }
    }
  `;
  document.head.appendChild(s);
}

// ─── Lifecycle ────────────────────────────────────────────────────────────────

export function renderLagerTab(): string {
  return `
    <section class="dash-content" style="padding:0;" id="lager-root">
      <p style="font-family:var(--font-mono);font-size:12px;color:var(--text-muted);text-align:center;padding:var(--space-8) 0;letter-spacing:0.08em;">LADE...</p>
    </section>
  `;
}

export function mountLagerTab(): void {
  addStyles();
  void reload();
}

export function cleanupLager(): void {
  catalogData = null;
  loadError   = null;
}
